import React, { useState } from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Divider, Flex, Collapse, Tag
} from "@chakra-ui/react";
import {
  FaTint, FaArrowRight, FaExclamationTriangle, FaChevronDown,
  FaChevronUp, FaShieldAlt, FaInfoCircle, FaSearch
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

interface AccordionItemProps {
  heading: string;
  tag: string;
  tagColor: string;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ heading, tag, tagColor, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="xl" overflow="hidden">
      <Box
        as="button"
        w="full"
        textAlign="left"
        p={5}
        onClick={() => setOpen(!open)}
        bg={open ? ICE_MIST : "white"}
        _hover={{ bg: ICE_MIST }}
        transition="background 0.2s"
      >
        <Flex justify="space-between" align="center" gap={4}>
          <HStack spacing={3} flex={1} flexWrap="wrap">
            <Text
              fontSize="xs"
              fontWeight="700"
              px={2}
              py={0.5}
              borderRadius="full"
              bg={tagColor + "20"}
              color={tagColor}
              whiteSpace="nowrap"
            >
              {tag}
            </Text>
            <Text fontWeight="600" color={NAVY} fontSize="sm">{heading}</Text>
          </HStack>
          <Icon
            as={open ? FaChevronUp : FaChevronDown}
            color={DEEP_AQUA}
            flexShrink={0}
            boxSize={3}
          />
        </Flex>
      </Box>
      <Collapse in={open}>
        <Box p={5} pt={0} bg="white" borderTop="1px solid" borderColor="gray.100">
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

const WaterBlogSeqwater: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/blog/seqwater-annual-report-2026"
        fallbackTitle="Seqwater Annual Report 2026: What It Shows — and What It Doesn't — One Water Australia"
        fallbackDescription="A critical analysis of the 2026 Seqwater drinking water quality report. What the data reveals, what it omits, and what SE Queensland residents should know about their tap water."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 14, md: 20 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.md" position="relative" zIndex={1}>
          <VStack spacing={5} textAlign="center">
            <Text fontSize="sm" fontWeight="700" color={WATER_BLUE} textTransform="uppercase" letterSpacing="wider">
              Critical Analysis
            </Text>
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Seqwater Annual Report 2026: What It Shows — and What It Doesn't
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.800" maxW="2xl">
              Seqwater's annual water quality report is the primary source of drinking water data for 3.5 million Queenslanders. Here's an honest reading of what that document tells you, what it's designed to tell you, and what it leaves out.
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Tag bg={WATER_BLUE + "30"} color={WATER_BLUE} fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                Investigative
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                ~14 min read
              </Tag>
              <Tag bg="whiteAlpha.200" color="white" fontWeight="600" fontSize="xs" px={3} py={1} borderRadius="full">
                17 citations
              </Tag>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Featured image */}
      <Box h={{ base: "240px", md: "420px" }} overflow="hidden">
        <Box
          as="img"
          src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80"
          alt="South East Queensland water catchment — Seqwater supply analysis"
          w="full"
          h="full"
          objectFit="cover"
          objectPosition="center"
        />
      </Box>

      <Container maxW="container.md" py={16}>
        <VStack spacing={12} align="stretch">

          {/* What Seqwater is */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Understanding What You're Reading</Heading>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Seqwater is the Queensland government bulk water authority responsible for sourcing, treating, and supplying water across South East Queensland — serving over 3.5 million people across Brisbane, Gold Coast, Sunshine Coast, Ipswich, Toowoomba, and surrounding areas via the SEQ Water Grid.
            </Text>
            <Text color="gray.700" mb={4} lineHeight="tall">
              Their annual drinking water quality report is the primary public document residents have access to. For most people, it functions as confirmation that water is safe. It's worth understanding the structure of that confirmation before accepting it uncritically.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <Box p={5} bg={ICE_MIST} borderRadius="xl" border="1px solid" borderColor={`${WATER_BLUE}40`}>
                <HStack mb={2}>
                  <Icon as={FaShieldAlt} color={DEEP_AQUA} />
                  <Text fontWeight="700" color="gray.800" fontSize="sm">What the report is</Text>
                </HStack>
                <VStack align="stretch" spacing={1}>
                  {[
                    "Compliance reporting against ADWG guideline values",
                    "Treatment-point and distribution-network monitoring",
                    "The most comprehensive public data on SE QLD supply",
                    "Aggregated zone-level averages for regulated parameters",
                    "A genuine record of the majority of quality parameters",
                  ].map((p) => (
                    <Text key={p} fontSize="xs" color="gray.700">✓ {p}</Text>
                  ))}
                </VStack>
              </Box>
              <Box p={5} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                <HStack mb={2}>
                  <Icon as={FaExclamationTriangle} color={DEEP_AQUA} />
                  <Text fontWeight="700" color="gray.800" fontSize="sm">What the report is not</Text>
                </HStack>
                <VStack align="stretch" spacing={1}>
                  {[
                    "Independent third-party verification",
                    "Tap-level monitoring at individual properties",
                    "A comprehensive screen for all known contaminants",
                    "A guarantee of what reaches your specific household",
                    "Required to address unscheduled or emerging contaminants",
                  ].map((p) => (
                    <Text key={p} fontSize="xs" color="gray.700">✗ {p}</Text>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`}>
              <HStack spacing={2} mb={2}>
                <Icon as={FaInfoCircle} color={DEEP_AQUA} />
                <Text fontWeight="700" color={NAVY} fontSize="sm">The self-reporting structure</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                Seqwater tests its own water and reports on its own compliance. The Australian Drinking Water Guidelines (ADWG) are guidelines, not legally enforceable standards under federal law — enforcement and oversight frameworks are administered at the state level and vary. There is no independent federal regulator that verifies the results at the tap. This is not a criticism unique to Seqwater — it applies to every Australian water utility — but it's important context for how the report should be read.
              </Text>
            </Box>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Key parameters table — honest version */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What the 2026 Report Shows: A Plain-English Reading</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              The following table translates Seqwater's key reported parameters — but with an additional column that most summaries omit: where genuine scientific uncertainty or community concern exists alongside official compliance.
            </Text>
            <Box overflowX="auto" mb={4}>
              <Table size="sm" variant="simple">
                <Thead bg={ICE_MIST}>
                  <Tr>
                    <Th color={NAVY} py={4} minW="120px">Parameter</Th>
                    <Th color={NAVY} py={4}>ADWG Value</Th>
                    <Th color={NAVY} py={4}>Typical SE QLD</Th>
                    <Th color={NAVY} py={4} minW="160px">Official status</Th>
                    <Th color={NAVY} py={4} minW="200px">Areas of uncertainty</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    {
                      param: "Chloramine residual",
                      guideline: "0.5–3 mg/L (as Cl₂)",
                      range: "0.5–1.5 mg/L",
                      status: "Within guidelines",
                      uncertainty: "Seqwater uses monochloramine — not free chlorine — since early 2000s. Most residents are unaware of the switch. Chloramine forms different DBPs (NDMA, iodoacids) that are not currently covered by ADWG guideline values.",
                    },
                    {
                      param: "Total THMs",
                      guideline: "≤0.25 mg/L",
                      range: "Typically <0.1 mg/L",
                      status: "Within guidelines",
                      uncertainty: "Reported as zone averages at distribution points. THM concentration increases with pipe residence time — end-of-network properties and homes with long service lines may have higher actual levels. Seasonal variation with organic matter load not always reflected in single-point reporting.",
                    },
                    {
                      param: "Fluoride",
                      guideline: "≤1.5 mg/L",
                      range: "0.6–1.0 mg/L",
                      status: "Within guidelines. Intentionally dosed.",
                      uncertainty: "The 2024 NTP systematic review (US National Toxicology Program) found moderate confidence that fluoride is associated with lower IQ in children at levels above 1.5 mg/L, and potentially at lower levels. Green et al. (JAMA Pediatrics, 2020) found associations in a Canadian fluoridated population study. Australian dosing is below the NTP threshold, but the margin is narrower than previously assumed, and the science is not settled.",
                    },
                    {
                      param: "Turbidity",
                      guideline: "≤5 NTU treated",
                      range: "<1 NTU (normal)",
                      status: "Within guidelines under standard conditions",
                      uncertainty: "During major rain events and flood periods, source water turbidity spikes significantly. The 2010–11 SE QLD floods produced exceptional source water turbidity challenges. Extended treatment periods are required. Reporting typically reflects normal operating conditions rather than event periods.",
                    },
                    {
                      param: "pH",
                      guideline: "6.5–8.5",
                      range: "7.0–8.0",
                      status: "Within guidelines",
                      uncertainty: "pH affects lead mobilisation from service lines and internal plumbing. Softer, slightly acidic water (common from mountain catchments serving Brisbane north) is more corrosive to copper and lead solder than harder, more alkaline water. pH at the tap may differ from distribution point measurement.",
                    },
                    {
                      param: "Hardness",
                      guideline: "No health guideline",
                      range: "50–250 mg/L (zone-dependent)",
                      status: "No compliance threshold",
                      uncertainty: "Varies significantly by source and zone. Gold Coast desalination blending changes hardness seasonally. Hard water areas (Toowoomba, parts of Gold Coast hinterland) associated with scaling of appliances. No ADWG health guideline exists, so hardness is not a compliance concern but may affect household outcomes.",
                    },
                    {
                      param: "PFAS",
                      guideline: "No current ADWG value",
                      range: "Not routinely reported",
                      status: "Not in standard report",
                      uncertainty: "PFAS contamination near RAAF Amberley (Ipswich) and Oakey (Darling Downs) has been extensively documented by the Department of Defence and Queensland Health. Seqwater has conducted voluntary PFAS sampling at some treatment works. The absence of PFAS from the standard annual report does not mean it has been ruled out — it means it is not yet a mandated reporting parameter.",
                    },
                    {
                      param: "Cyanotoxins",
                      guideline: "≤1.3 µg/L (microcystin)",
                      range: "Varies by bloom event",
                      status: "Monitored during algal alerts",
                      uncertainty: "Lake Wivenhoe and other SEQ dams have experienced cyanobacterial bloom events. Seqwater activates alert protocols and activates activated carbon treatment during events. Monitoring is event-driven, not continuous. The 2019 Wivenhoe algal alert was publicly reported; others may not be. Treatment is typically effective — but the source water risk is real and varies with catchment conditions.",
                    },
                    {
                      param: "Microplastics",
                      guideline: "No ADWG guideline",
                      range: "Not monitored",
                      status: "Not in report",
                      uncertainty: "A 2022 study (Leslie et al., Environment International) detected microplastics in human blood. WHO (2022) reviewed microplastics in drinking water as an emerging concern. No ADWG guideline value exists. Australian tap water studies are limited, but international studies consistently detect microplastics in treated tap water.",
                    },
                  ].map((row) => (
                    <Tr key={row.param} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="600" color={NAVY} fontSize="xs">{row.param}</Td>
                      <Td fontSize="xs" color="gray.600">{row.guideline}</Td>
                      <Td fontSize="xs" color={DEEP_AQUA} fontWeight="500">{row.range}</Td>
                      <Td fontSize="xs" color="gray.600">{row.status}</Td>
                      <Td fontSize="xs" color="gray.600" lineHeight="tall">{row.uncertainty}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Text fontSize="xs" color="gray.500">
              Ranges are indicative based on published Seqwater data and independent analysis. Verify against the current published report for your specific supply zone. ADWG = Australian Drinking Water Guidelines.
            </Text>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Gaps in depth */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>The Gaps: What Deserves Closer Examination</Heading>
            <VStack spacing={3} align="stretch">

              <AccordionItem heading="PFAS in the SEQ Catchment: What We Know and Don't" tag="Documented concern" tagColor={NAVY}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    PFAS contamination from firefighting foam (AFFF) used at RAAF Base Amberley near Ipswich is among the most extensively documented PFAS sites in Australia. Queensland Health and the Department of Defence have both conducted and published sampling. Groundwater in the Amberley and Rosewood corridors has returned elevated PFAS readings.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Seqwater's supply in this area comes primarily from surface water (Wivenhoe, Somerset, and the SEQ Grid) rather than affected groundwater. However, runoff from PFAS-contaminated land can enter surface water catchments during rain events, and the fate of PFAS in SEQ drinking water catchments is not comprehensively characterised in publicly available data.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Seqwater has publicly stated it has conducted PFAS testing at treatment works and found levels below health guideline values — but Australia's health guideline values for PFAS in drinking water (70 ng/L PFOS+PFOA, ADWG 2022) are higher than the US EPA's updated maximum contaminant levels (4 ng/L individual PFOA/PFOS, 2024). The threshold at which "below guidelines" means "safe" is itself contested internationally.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Department of Defence PFAS Investigation — RAAF Amberley (2016–2021); Queensland Health PFAS Monitoring Program; US EPA PFAS National Primary Drinking Water Regulation (April 2024); ADWG 2022 Technical Documents — PFAS
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="The Disinfection Byproduct Reporting Gap" tag="Regulatory gap" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Total THMs appear in Seqwater's annual water quality data, typically aggregated by zone and presented against the ADWG guideline of 0.25 mg/L. In most zones, reported values are well below this threshold.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    What the public-facing report does not include: individual THM species breakdown (chloroform vs bromodichloromethane vs others have different toxicological profiles), haloacetic acid (HAA) data with the same prominence, chloramine-specific DBPs (NDMA, iodoacids), or seasonal variation that reflects organic matter loading during high-rainfall periods.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The ADWG's HAA guideline values are set for individual compounds (DCA: 0.1 mg/L; TCA: 0.2 mg/L etc.), but HAA reporting is less standardised in Australian utility annual reports than THM reporting. The technical annexures to Seqwater's reports contain more detail, but they're rarely accessed by the public.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Critically: because Seqwater uses <Text as="span" fontWeight="600">chloramine</Text> rather than free chlorine, the DBP profile differs from what the ADWG's principal monitoring framework was designed around. NDMA and iodoacetic acids — formed specifically from chloramine reactions — have no ADWG guideline values despite being classified as probable or possible carcinogens by IARC.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: ADWG 2022; Richardson et al. (2007); Schreiber & Mitch (2006) — NDMA formation from chloramine; IARC Monograph Group 2A NDMA
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Cyanobacterial Blooms: The Source Water Story" tag="Event-driven risk" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    SE Queensland's major storage dams — Wivenhoe, Somerset, North Pine, Hinze — have all experienced cyanobacterial (blue-green algae) bloom events. Cyanobacteria produce toxins including microcystin, cylindrospermopsin, and saxitoxin. At sufficient concentrations, these are acutely toxic; at lower concentrations, chronic effects are less well characterised.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Seqwater implements a multi-alert system (Alert Level 1–3) and activates activated carbon treatment and increased monitoring during bloom events. The treatment is generally effective. However, "generally effective" and "always effective" are different claims, and the activated carbon capacity required scales with bloom severity.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The annual report typically notes whether bloom events occurred during the year. What it does not routinely publish is the specific cyanotoxin concentrations measured in source water during events, the treatment response efficacy data, or the post-treatment levels. This information exists internally — whether it should be more prominently in the public document is a legitimate question.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Warmer water temperatures, increased nutrient loading from agricultural runoff, and more frequent extreme weather events associated with climate variability all create conditions more favourable to cyanobacterial blooms. The trend in source water conditions warrants ongoing attention.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Seqwater Algal Alert Reports (published on Seqwater website); ADWG 2022 — Cyanobacteria and Cyanotoxins chapter; Burford, M. et al. — Cyanobacterial blooms in SEQ water bodies (various); WHO (2020) — Cyanobacterial toxins in drinking water
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="The Desalination Variable: Gold Coast's Shifting Chemistry" tag="Infrastructure complexity" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The Gold Coast Desalination Plant at Tugun (capacity: 133 ML/day) operates on a variable schedule, ramping up during drought periods and reducing output when dam levels are sufficient. The annual report notes this as a feature; fewer discussions address the consequence — the chemical composition of Gold Coast tap water changes depending on the desalination contribution.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Desalinated water (reverse osmosis permeate) is extremely low in minerals. Before distribution, it is remineralised to meet ADWG requirements and to reduce its corrosiveness. However, desalinated water blended with dam water produces a different mineral profile, TDS, hardness, and pH balance than either source individually. The ratios shift with operational decisions.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    For residents who have invested in filtration or who are managing health conditions sensitive to mineral intake, knowing when the desalination contribution is high is practically relevant. This information is not prominently communicated in real time.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Seqwater Gold Coast Desalination Plant Technical Information; SEQ Water Grid Operational Reports; Gold Coast Water Supply Zone data
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="The Last Mile: What Seqwater Can't Monitor" tag="Property-specific" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    Seqwater's monitoring jurisdiction ends at the connection point to the property service line. What happens between that point and your kitchen tap is not within their reporting framework — and this is not a minor gap.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Lead and copper:</Text> Properties built before 1989 (when lead was prohibited from solder in Australia) may have lead solder at pipe joints, or older brass fixtures with significant lead content. Even after the prohibition, brass fittings with up to 4.5% lead were permitted until 2013 under AS/NZS 4020. The chemistry of what's in the pipe matters — softer, more acidic water from mountain catchments is more corrosive to metallic plumbing than harder, more alkaline water.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Hot water systems:</Text> Storage hot water systems can accumulate sediment, provide conditions for Legionella growth at low temperatures, and create localised water quality conditions (elevated TDS, altered pH) that are entirely separate from network water quality.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Stagnation effects:</Text> In properties or apartments where a section of supply line is infrequently used, water sitting in copper or steel pipe accumulates dissolved metals over time. First-draw water from taps not recently used can have significantly different metal profiles from flushed water.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    <Text as="span" fontWeight="600">Apartment buildings:</Text> Multi-storey buildings typically have roof-mounted or basement storage tanks. These tanks require periodic cleaning and have their own contamination risk profile (sediment, biofilm, Legionella) that is entirely outside the Seqwater monitoring framework.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Australian Standard AS/NZS 4020 (testing of products in contact with drinking water); ADWG 2022 — Lead chapter; Hanna-Attisha et al. (2016) — lead in water infrastructure context; Edwards, M. (2004) — corrosion and plumbing chemistry
                  </Text>
                </VStack>
              </AccordionItem>

              <AccordionItem heading="Flood Events and Source Water Quality" tag="Climate-linked" tagColor={DEEP_AQUA}>
                <VStack align="stretch" spacing={3} pt={4}>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    The 2010–11 SE QLD floods were the largest water management event in Seqwater's operational history. Wivenhoe and Somerset Dams, designed for flood mitigation, received extraordinary inflows. Source water turbidity reached levels that stretched treatment capacity, and the decision-making around releases from Wivenhoe was later examined in the Queensland Floods Commission of Inquiry.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    What the annual report for that period documented: increased turbidity events and the treatment response. What it did not comprehensively characterise: the full suite of contaminants mobilised by floodwaters (agricultural chemicals, heavy metals, hydrocarbons, biological material) that entered supply catchments, the limits of activated carbon adsorption capacity during extended high-turbidity events, or the quality of water distributed during the transition periods.
                  </Text>
                  <Text fontSize="sm" color="gray.700" lineHeight="tall">
                    More recent La Niña periods (2020–2023) produced similarly elevated catchment inputs. Climate variability is expected to increase the frequency and severity of high-turbidity source water events in SEQ. The annual report framework — designed for normal operating conditions — is less informative about performance during these events.
                  </Text>
                  <Text fontSize="xs" color="gray.500" pt={2} borderTop="1px solid" borderColor="gray.100">
                    References: Queensland Floods Commission of Inquiry — Final Report (2012); Seqwater Drought Response and Emergency Plans; Bureau of Meteorology La Niña event records 2020–2023
                  </Text>
                </VStack>
              </AccordionItem>

            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Area-specific context */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>What This Means by Area</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              SE Queensland draws from multiple catchments and treatment plants. Your water quality profile — and the specific gaps in public reporting — vary significantly by zone.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              {[
                {
                  area: "Gold Coast",
                  source: "Hinze Dam + Tugun Desalination",
                  note: "Chemistry shifts seasonally with desalination contribution. Desalination periods produce lower-mineral, softer water. PFAS proximity to RAAF Amberley (Ipswich-adjacent supply) warrants monitoring. Older Southport, Burleigh, and Surfers Paradise infrastructure may introduce last-mile variables.",
                },
                {
                  area: "Brisbane North",
                  source: "Wivenhoe / Somerset Dams",
                  note: "Soft, low-TDS water from mountain catchments. More corrosive to lead solder and copper pipe than harder supplies. Homes pre-1989 particularly relevant. Subject to cyanobacterial bloom events in Wivenhoe during warm periods.",
                },
                {
                  area: "Ipswich / Western Corridor",
                  source: "Wivenhoe / SEQ Grid",
                  note: "Proximity to RAAF Amberley makes this the highest-priority area for PFAS attention. Seqwater has conducted PFAS testing here. Residents in the PFAS investigation area should access the Queensland Health monitoring data directly.",
                },
                {
                  area: "Sunshine Coast",
                  source: "Baroon Pocket Dam + Grid connections",
                  note: "Generally soft water. Algal bloom events documented in Baroon Pocket and connected storages. Grid interconnection means supply source can vary during drought periods. Hardness varies notably between coastal and hinterland zones.",
                },
                {
                  area: "Toowoomba",
                  source: "Cressbrook Dam + Recycled Water (purified)",
                  note: "Western Queensland water typically harder. Toowoomba operates one of Australia's longest-running advanced water recycling schemes (indirect potable reuse via Cooby Dam). Recycled water is treated to high standard but introduces specific chemistry considerations.",
                },
                {
                  area: "Moreton Bay / Northern Brisbane",
                  source: "North Pine Dam + Grid",
                  note: "North Pine Dam catchment includes rural land with fertiliser and agricultural runoff potential. Subject to algal alert events. End-of-network areas may have higher DBP levels from pipe residence time.",
                },
              ].map((area) => (
                <Box key={area.area} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} mb={1}>{area.area}</Text>
                  <Text fontSize="xs" color={DEEP_AQUA} fontWeight="600" mb={2}>{area.source}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{area.note}</Text>
                </Box>
              ))}
            </SimpleGrid>
            <Box>
              <Button
                as={RouterLink}
                to="/water/water-quality/gold-coast"
                variant="outline"
                borderColor={DEEP_AQUA}
                color={DEEP_AQUA}
                rightIcon={<Icon as={FaArrowRight} />}
                _hover={{ bg: ICE_MIST }}
                transition="all 0.2s"
              >
                Gold Coast Water Quality — Detailed Local Analysis
              </Button>
            </Box>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Community evidence */}
          <Box>
            <Heading size="lg" color={NAVY} mb={5}>Community Reports and Anecdotal Patterns</Heading>
            <Text color="gray.700" mb={6} lineHeight="tall">
              Seqwater's annual report is a technical document designed to demonstrate compliance. What it does not capture — and what is worth taking seriously — is the accumulated experience of residents who notice changes in their water.
            </Text>
            <VStack spacing={4} align="stretch">
              {[
                {
                  title: "Post-flood taste and odour complaints",
                  detail: "Following the 2011 and 2022 flood events, SE QLD community forums and local council submissions documented elevated taste and odour complaints that persisted beyond the acute event period. Treatment produces earthy and musty compounds (geosmin, 2-MIB) during algal bloom events; these are detectable by smell and taste at very low concentrations (nanogram per litre range) well below any health concern threshold, but they function as a signal that source water conditions have changed.",
                },
                {
                  title: "Chloramine transition reports",
                  detail: "Seqwater's switch to chloramine disinfection was not widely communicated to the public. Communities in various QLD forums have documented the experience of noticing a change in water smell (chloramine has a distinct 'band-aid' or ammonia-adjacent odour profile at certain concentrations), discovering their standard water filter is not removing it, and aquarium fish deaths from standard dechlorination tablets that don't neutralise chloramine.",
                },
                {
                  title: "Hardness variation and appliance damage",
                  detail: "Gold Coast residents receiving higher proportions of desalinated water report markedly different limescale patterns than those in high-dam-water periods. While not a health issue, this variation is not explained in Seqwater communications and creates practical confusion about appliance maintenance and filtration choice.",
                },
                {
                  title: "The Williamtown pattern: community observation before regulatory acknowledgment",
                  detail: "While Williamtown is not in Seqwater's supply area, the pattern is instructive. Defence contamination of groundwater was observed and reported by the community for years before regulatory acknowledgment. Near Amberley, community residents have reported concerns about water and soil quality through the PFAS investigation process. The credibility of community observation as an early signal — ahead of formal monitoring — deserves acknowledgment.",
                },
              ].map((item) => (
                <Box key={item.title} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <Text fontWeight="700" color={NAVY} fontSize="sm" mb={2}>{item.title}</Text>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </VStack>
          </Box>

          <Divider borderColor="gray.200" />

          {/* What a full picture looks like */}
          <Box>
            <Heading size="lg" color={NAVY} mb={3}>What a More Complete Picture Requires</Heading>
            <Box p={5} bg={ICE_MIST} borderRadius="xl" border={`1px solid ${WATER_BLUE}30`} mb={6}>
              <HStack mb={2}>
                <Icon as={FaSearch} color={DEEP_AQUA} />
                <Text fontWeight="700" color={NAVY} fontSize="sm">The Seqwater report is the starting point. Here's what else to consider.</Text>
              </HStack>
              <Text fontSize="sm" color="gray.700" lineHeight="tall">
                For most households, the Seqwater data is reassuring on its own terms. But the framing of this article matters: compliance with guidelines is not the same as absence of risk, and guidelines are not the same as law. A genuinely complete picture of your tap water quality requires additional information.
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {[
                {
                  step: "Check the full technical annexures",
                  detail: "Seqwater publishes detailed water quality data beyond the annual report summary. Zone-level data, individual parameter breakdowns, and compliance tables are available via the Seqwater website and Queensland Open Data Portal.",
                  icon: FaSearch,
                },
                {
                  step: "Identify your supply zone and source",
                  detail: "Use Seqwater's zone mapping to confirm which dam and treatment plant supplies your address, and whether desalination contributes to your supply. This determines which zone-specific data applies to you.",
                  icon: FaTint,
                },
                {
                  step: "Check Queensland Health PFAS data if relevant",
                  detail: "If you're in the Ipswich, Amberley, or adjacent corridors, Queensland Health has published PFAS-specific monitoring results. This is separate from the Seqwater drinking water report.",
                  icon: FaExclamationTriangle,
                },
                {
                  step: "Consider a property-level test",
                  detail: "A tap-water test from an accredited NATA laboratory is the only way to know what's actually reaching your household. This captures last-mile variables (building plumbing, hot water system, first-draw vs flushed) that the network data cannot.",
                  icon: FaShieldAlt,
                },
              ].map((item) => (
                <Box key={item.step} p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl">
                  <HStack mb={3}>
                    <Icon as={item.icon} color={DEEP_AQUA} />
                    <Text fontWeight="700" color={NAVY} fontSize="sm">{item.step}</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" lineHeight="tall">{item.detail}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Divider borderColor="gray.200" />

          {/* Closing position */}
          <Box p={8} bg={ICE_MIST} borderRadius="2xl" border={`1px solid ${WATER_BLUE}30`}>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color={NAVY}>Our Reading of the Seqwater Report</Heading>
              <Text color="gray.700" fontStyle="italic" lineHeight="tall" fontSize="lg">
                "Seqwater produces one of the more thorough regional water quality reports in Australia. The data shows a well-managed system that meets its regulatory requirements. What we'd like to see: more prominent reporting on chloramine-specific DBPs including NDMA, clearer communication about the disinfectant switch and its filtration implications, event-period data for algal bloom and flood events, and a more proactive approach to PFAS characterisation across the full supply network. These are not accusations of concealment — they're the difference between a compliance document and a genuinely informative public health report."
              </Text>
              <Text fontSize="xs" color="gray.500" fontStyle="normal">— One Water Australia</Text>
            </VStack>
          </Box>

          {/* Sources */}
          <Box>
            <Heading size="sm" color={NAVY} mb={4}>Sources & References</Heading>
            <VStack align="flex-start" spacing={1}>
              {[
                "Seqwater Annual Drinking Water Quality Report 2025–26 (seqwater.com.au)",
                "Australian Drinking Water Guidelines (ADWG), NHMRC & NRMMC 2011 (updated 2022)",
                "Queensland Open Data Portal — Seqwater Water Quality Data",
                "Queensland Health — PFAS Monitoring in SE QLD (Amberley Investigation Area)",
                "Department of Defence — PFAS Site Investigation, RAAF Base Amberley (2016–2021)",
                "US EPA — PFAS National Primary Drinking Water Regulation, April 2024",
                "Richardson, S.D. et al. — Occurrence, Genotoxicity, and Carcinogenicity of Regulated and Emerging DBPs. Mutat Res. 2007",
                "Schreiber, I.M. & Mitch, W.A. — Nitrosamine Formation Pathway Revisited. Environ Sci Technol. 2006",
                "National Toxicology Program (NTP) — Systematic Review: Fluoride Exposure and Neurodevelopmental and Cognitive Health Effects (2024)",
                "Green, R. et al. — Association Between Maternal Fluoride Exposure During Pregnancy and IQ Scores in Offspring. JAMA Pediatrics. 2020",
                "Leslie, H.A. et al. — Discovery and Quantification of Plastic Particle Pollution in Human Blood. Environment International. 2022",
                "WHO (2022) — Microplastics in Drinking Water: An Emerging Concern",
                "Seqwater — Algal Alert Historical Records (published on Seqwater website)",
                "WHO (2020) — Cyanobacterial Toxins — Guidance for Drinking Water Quality",
                "Burford, M. et al. — Understanding Cyanobacterial Bloom Dynamics in Subtropical Reservoirs (2016)",
                "Queensland Floods Commission of Inquiry — Final Report (2012)",
                "Edwards, M. et al. — Corrosion of Lead by Chloramines in Drinking Water. J AWWA. 2004",
              ].map((s) => (
                <Text key={s} fontSize="xs" color="gray.500" lineHeight="tall">• {s}</Text>
              ))}
            </VStack>
          </Box>

          {/* CTA */}
          <Box bg={NAVY} p={10} borderRadius="2xl" textAlign="center">
            <VStack spacing={5}>
              <Heading size="lg" color="white">Want to know what's reaching your tap — not just your zone?</Heading>
              <Text color="whiteAlpha.800" maxW="lg">
                Seqwater's data tells you about your regional supply. A property-level assessment tells you what's actually in your glass — including last-mile variables the network data can't capture.
              </Text>
              <HStack spacing={4} flexWrap="wrap" justify="center">
                <Button
                  as={RouterLink}
                  to="/contact"
                  bg={WATER_BLUE}
                  color={NAVY}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "#8DC5E0" }}
                  transition="all 0.2s"
                >
                  Book a Free Water Assessment
                </Button>
                <Button
                  as={RouterLink}
                  to="/water/water-quality/gold-coast"
                  variant="outline"
                  borderColor={WATER_BLUE}
                  color={WATER_BLUE}
                  size="lg"
                  fontWeight="bold"
                  _hover={{ bg: "whiteAlpha.100" }}
                >
                  Gold Coast Water Quality Detail
                </Button>
              </HStack>
            </VStack>
          </Box>

        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterBlogSeqwater;
